const KEY = "cave-critter-music";
const SCALE = [146.83, 174.61, 196.0, 220.0, 261.63, 293.66, 349.23, 392.0];
const PATTERN = [0, -1, 2, -1, 4, -1, 3, -1, 5, -1, -1, 4, -1, 2, -1, -1, 1, -1, 3, -1, 4, -1, -1, -1, 6, -1, 4, -1, 2, -1, 0, -1, 3, -1, -1, 5, -1, 7, -1, -1, 4, -1, 2, -1, -1, -1, 0, -1];
const STEP = 0.55;
const TUNNEL_SCALE = [110.0, 116.54, 130.81, 146.83, 164.81, 174.61, 220.0, 233.08];
const TUNNEL_PATTERN = [0, -1, 1, 0, -1, 3, -1, 0, 2, -1, 1, -1, 0, -1, 4, 3, 0, -1, 1, 0, -1, 5, -1, 3, 2, -1, 1, -1, 0, -1, 7, 6];
const TUNNEL_STEP = 0.3;
const INTRO_SCALE = [261.63, 293.66, 329.63, 392.0, 440.0, 523.25, 587.33, 659.25];
const INTRO_PATTERN = [0, -1, 2, -1, 4, -1, -1, 3, -1, 2, -1, -1, 5, -1, 4, -1, -1, 2, -1, 0, -1, -1, -1, 1, -1, 3, -1, 4, -1, -1, 7, -1, 6, -1, 4, -1, -1, 3, -1, 2, -1, -1, -1, -1];
const INTRO_STEP = 0.62;
const TRAILER_SCALE = [261.63, 293.66, 329.63, 392.0, 440.0, 523.25, 587.33, 659.25, 783.99];
const TRAILER_PATTERN = [0, 2, 4, 5, 4, 2, 0, -1, 1, 3, 5, 7, 5, 3, 1, -1, 2, 4, 7, 8, 7, 4, 2, -1, 5, 4, 3, 2, 1, 0, -1, -1, 4, 4, 5, 7, -1, 7, 5, 4, 2, 2, 3, 5, -1, 5, 3, 2, 0, 2, 4, 7, 8, 7, 4, 2, 1, -1, 1, -1, 0, -1, -1, -1];
const TRAILER_BASS = [0, 0, 3, 3, 1, 1, 4, 4];
const TRAILER_STEP = 0.19;

export type MusicMode = "cave" | "tunnel" | "intro" | "trailer";

const gainFor = (mode: MusicMode) => (mode === "tunnel" ? 0.55 : mode === "intro" ? 0.7 : mode === "trailer" ? 1.1 : 1);

export class Music {
  enabled = localStorage.getItem(KEY) !== "0";
  volume = Number(localStorage.getItem("cave-critter-music-vol") ?? "1");
  setVolume(v: number) {
    this.volume = v; localStorage.setItem("cave-critter-music-vol", String(v));
    if (!this.master) return;
    const ac = this.ac;
    this.master.gain.cancelScheduledValues(ac.currentTime);
    this.master.gain.setValueAtTime(Math.max(0.0001, this.master.gain.value), ac.currentTime);
    this.master.gain.linearRampToValueAtTime(this.vol(this.mode), ac.currentTime + 0.15);
  }
  private vol(mode: MusicMode) { return Math.max(0.0001, gainFor(mode) * this.volume); }
  mode: MusicMode = "cave";
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private echo: DelayNode | null = null;
  private timer: number | null = null;
  private nextTime = 0;
  private step = 0;
  private drones: OscillatorNode[] = [];

  private get ac() {
    if (!this.ctx) this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    return this.ctx;
  }

  get running() { return !!this.ctx && this.ctx.state === "running"; }
  resume() { this.ctx?.resume().catch(() => {}); }

  setEnabled(v: boolean) {
    this.enabled = v;
    localStorage.setItem(KEY, v ? "1" : "0");
    if (v) this.start(); else this.stop();
  }

  setMode(mode: MusicMode) {
    if (this.mode === mode) return;
    this.mode = mode;
    this.step = 0;
    if (!this.master) return;
    const ac = this.ac;
    this.master.gain.cancelScheduledValues(ac.currentTime);
    this.master.gain.setValueAtTime(Math.max(0.0001, this.master.gain.value), ac.currentTime);
    this.master.gain.exponentialRampToValueAtTime(this.vol(mode), ac.currentTime + 1.2);
  }

  start() {
    if (!this.enabled || this.timer !== null) return;
    const ac = this.ac;
    if (ac.state === "suspended") ac.resume();
    this.master = ac.createGain();
    this.master.gain.setValueAtTime(0.0001, ac.currentTime);
    this.master.gain.exponentialRampToValueAtTime(this.vol(this.mode), ac.currentTime + 2);
    this.master.connect(ac.destination);

    this.echo = ac.createDelay(1.5);
    this.echo.delayTime.value = 0.42;
    const fb = ac.createGain();
    fb.gain.value = 0.38;
    const lp = ac.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 1400;
    this.echo.connect(fb).connect(lp).connect(this.echo);
    this.echo.connect(this.master);

    const droneOut = ac.createBiquadFilter();
    droneOut.type = "lowpass";
    droneOut.frequency.value = 260;
    const droneGain = ac.createGain();
    droneGain.gain.value = 0.045;
    droneOut.connect(droneGain).connect(this.master);
    const lfo = ac.createOscillator();
    const lfoGain = ac.createGain();
    lfo.frequency.value = 0.08;
    lfoGain.gain.value = 0.02;
    lfo.connect(lfoGain).connect(droneGain.gain);
    lfo.start();
    this.drones = [lfo];
    [55, 73.42, 110.5].forEach((f, i) => {
      const o = ac.createOscillator();
      o.type = i === 2 ? "sine" : "triangle";
      o.frequency.value = f;
      o.detune.value = i * 4;
      o.connect(droneOut);
      o.start();
      this.drones.push(o);
    });

    this.step = 0;
    this.nextTime = ac.currentTime + 0.3;
    this.timer = window.setInterval(() => this.schedule(), 200);
  }

  stop() {
    if (this.timer !== null) { clearInterval(this.timer); this.timer = null; }
    const ac = this.ctx;
    if (!ac || !this.master) return;
    const m = this.master;
    m.gain.cancelScheduledValues(ac.currentTime);
    m.gain.setValueAtTime(m.gain.value, ac.currentTime);
    m.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + 0.8);
    const drones = this.drones;
    window.setTimeout(() => { drones.forEach((d) => { try { d.stop(); } catch {} }); m.disconnect(); }, 900);
    this.drones = [];
    this.master = null;
    this.echo = null;
  }

  private schedule() {
    const ac = this.ac;
    if (this.mode === "trailer") { this.scheduleTrailer(); return; }
    const tunnel = this.mode === "tunnel";
    const intro = this.mode === "intro";
    const pattern = tunnel ? TUNNEL_PATTERN : intro ? INTRO_PATTERN : PATTERN;
    const scale = tunnel ? TUNNEL_SCALE : intro ? INTRO_SCALE : SCALE;
    const stepLen = tunnel ? TUNNEL_STEP : intro ? INTRO_STEP : STEP;
    while (this.nextTime < ac.currentTime + 0.5) {
      const note = pattern[this.step % pattern.length];
      if (note >= 0) this.pluck(scale[note], this.nextTime, tunnel);
      if (intro && this.step % 8 === 0) this.pad(scale[0] / 2, this.nextTime, stepLen * 8);
      if (tunnel && this.step % 4 === 0) this.thump(this.nextTime);
      if (!tunnel && !intro && Math.random() < 0.06) this.drip(this.nextTime + Math.random() * stepLen);
      this.nextTime += stepLen;
      this.step++;
    }
  }

  private scheduleTrailer() {
    const ac = this.ac;
    while (this.nextTime < ac.currentTime + 0.5) {
      const t = this.nextTime, i = this.step;
      const note = TRAILER_PATTERN[i % TRAILER_PATTERN.length];
      if (note >= 0) this.lead(TRAILER_SCALE[note], t);
      if (i % 4 === 0) { this.thump(t); this.bass(TRAILER_SCALE[TRAILER_BASS[Math.floor(i / 4) % TRAILER_BASS.length]] / 2, t); }
      if (i % 4 === 2) this.noise(t, 0.12, 1800, 0.07);
      if (i % 2 === 1) this.noise(t, 0.04, 7000, 0.035);
      this.nextTime += TRAILER_STEP;
      this.step++;
    }
  }

  private lead(freq: number, t: number) {
    const ac = this.ac;
    if (!this.master) return;
    const o = ac.createOscillator();
    const o2 = ac.createOscillator();
    const g = ac.createGain();
    o.type = "triangle"; o2.type = "square";
    o.frequency.value = freq; o2.frequency.value = freq;
    o2.detune.value = 6;
    const mix = ac.createGain();
    mix.gain.value = 0.07;
    o2.connect(mix).connect(g);
    o.connect(g);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.11, t + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.34);
    g.connect(this.master);
    o.start(t); o2.start(t); o.stop(t + 0.4); o2.stop(t + 0.4);
  }

  private bass(freq: number, t: number) {
    const ac = this.ac;
    if (!this.master) return;
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = "sawtooth";
    o.frequency.value = freq / 2;
    const lp = ac.createBiquadFilter();
    lp.type = "lowpass"; lp.frequency.value = 420;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.13, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + TRAILER_STEP * 3.6);
    o.connect(lp).connect(g).connect(this.master);
    o.start(t); o.stop(t + TRAILER_STEP * 4);
  }

  private noise(t: number, len: number, freq: number, vol: number) {
    const ac = this.ac;
    if (!this.master) return;
    const buf = ac.createBuffer(1, Math.ceil(ac.sampleRate * len), ac.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const src = ac.createBufferSource();
    src.buffer = buf;
    const f = ac.createBiquadFilter();
    f.type = freq > 4000 ? "highpass" : "bandpass"; f.frequency.value = freq;
    const g = ac.createGain();
    g.gain.value = vol;
    src.connect(f).connect(g).connect(this.master);
    src.start(t);
  }

  private pad(freq: number, t: number, len: number) {
    const ac = this.ac;
    if (!this.master) return;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.035, t + len * 0.4);
    g.gain.exponentialRampToValueAtTime(0.0001, t + len);
    [1, 1.5, 2].forEach((m, i) => {
      const o = ac.createOscillator();
      o.type = i === 0 ? "sine" : "triangle";
      o.frequency.value = freq * m;
      o.detune.value = i * 3;
      o.connect(g);
      o.start(t); o.stop(t + len + 0.1);
    });
    g.connect(this.master);
  }

  private thump(t: number) {
    const ac = this.ac;
    if (!this.master) return;
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(70, t);
    o.frequency.exponentialRampToValueAtTime(38, t + 0.18);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.16, t + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
    o.connect(g).connect(this.master);
    o.start(t);
    o.stop(t + 0.3);
  }

  private pluck(freq: number, t: number, tense = false) {
    const ac = this.ac;
    if (!this.master || !this.echo) return;
    const o = ac.createOscillator();
    const o2 = ac.createOscillator();
    const g = ac.createGain();
    o.type = tense ? "triangle" : "sine";
    o2.type = tense ? "sawtooth" : "triangle";
    o.frequency.value = freq;
    o2.frequency.value = freq * 2;
    const mix = ac.createGain();
    mix.gain.value = tense ? 0.08 : 0.25;
    o2.connect(mix).connect(g);
    o.connect(g);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(tense ? 0.06 : 0.09, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t + (tense ? 0.5 : 1.6));
    g.connect(this.master);
    g.connect(this.echo);
    o.start(t); o2.start(t);
    o.stop(t + 1.7); o2.stop(t + 1.7);
  }

  private drip(t: number) {
    const ac = this.ac;
    if (!this.master || !this.echo) return;
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(1900 + Math.random() * 600, t);
    o.frequency.exponentialRampToValueAtTime(900, t + 0.12);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.05, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.15);
    o.connect(g).connect(this.echo);
    o.start(t);
    o.stop(t + 0.2);
  }
}
